// Configuración del usuario para toda la plataforma nueva: features (permisos de módulos), organización y
// sesión. Es la misma fuente que la plataforma de siempre (/api/user/config, ver App.js → loadUserConfig):
// el backend decide qué módulos tiene cada quien y vuelve a validar en cada endpoint.
//
// Contrato (lo usan todos los módulos; sólo se AGREGAN campos):
//   { user, username, signOut, features, featuresListas, config, error,
//     esAdmin, organizacion: { nombre, logo }, featuresDelServidor }
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useOrganization } from '../../contexts/OrganizationContext';
import { installGlobalErrorReporter } from '../../utils/errorReporter';
import { branding, isModuleHidden } from '../../config/branding';

export const API_URL = process.env.REACT_APP_STRANDS_API_URL || 'http://localhost:5001';
const PREFIJO_CACHE = 'db-plataforma:features';
const REINTENTOS_MS = [3000, 10000];

const ConfigUsuarioContext = createContext(null);

// La caché es por usuario: en un navegador compartido, el siguiente no ve por un instante el menú del anterior.
const llaveCache = (username) => `${PREFIJO_CACHE}:${username}`;

function featuresGuardadas(username) {
  if (!username) return null;
  try { return JSON.parse(localStorage.getItem(llaveCache(username))) || null; } catch { return null; }
}

/** Módulos que oculta el dominio (config/branding.js). En knobloch.data-bunker.ai sólo quedan los suyos.
 *  En el dominio por defecto la de siempre esconde Knobloch; la plataforma nueva lo muestra a quien tenga el flag
 *  (sólo lo tienen sus usuarios y la demo). */
export function ocultoPorDominio(feature) {
  if (!feature) return false;
  if (branding.key === 'default') return false;
  return isModuleHidden(feature);
}

function recortarPorDominio(features) {
  if (!features) return features;
  const salida = { ...features };
  Object.keys(salida).forEach((k) => { if (ocultoPorDominio(k)) salida[k] = false; });
  // Módulos que el dominio oculta aunque la respuesta no traiga la llave.
  ['chatAgent', 'powerbiReports', 'mediaMonitoring', 'priceAlerts', 'opportunities', 'visuals', 'visualsDemo',
    'insights', 'knobloch', 'lorealPricing', 'visualesNike', 'visualesTiendasBara', 'inicio'].forEach((k) => { if (ocultoPorDominio(k)) salida[k] = false; });
  return salida;
}

export function ConfigUsuarioProvider({ user, signOut, children }) {
  const { updateBrandingFromUserConfig } = useOrganization();
  const username = user?.username;
  // Con caché, el menú aparece al instante; la respuesta del servidor la reemplaza.
  const [features, setFeatures] = useState(() => featuresGuardadas(username));
  const [featuresDelServidor, setFeaturesDelServidor] = useState(null);
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => installGlobalErrorReporter(username), [username]);

  useEffect(() => {
    if (!username) return undefined;
    let vivo = true;
    let espera = null;
    const ctrl = new AbortController();
    const cargar = (intento) => {
      fetch(`${API_URL}/api/user/config?username=${encodeURIComponent(username)}`, { signal: ctrl.signal })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
        .then((data) => {
          if (!vivo || data.success === false) return;
          updateBrandingFromUserConfig(data);
          setConfig(data);
          setError(null);
          if (data.features) {
            setFeatures(data.features);
            setFeaturesDelServidor(data.features);
            try { localStorage.setItem(llaveCache(username), JSON.stringify(data.features)); } catch { /* ignorar */ }
          }
        })
        .catch((e) => {
          if (!vivo || e.name === 'AbortError') return;
          setError(e.message);
          // Sin respuesta no se bloquea la plataforma: se queda con lo último conocido y reintenta.
          setFeatures((f) => f ?? {});
          if (intento < REINTENTOS_MS.length) espera = setTimeout(() => cargar(intento + 1), REINTENTOS_MS[intento]);
        });
    };
    cargar(0);
    return () => { vivo = false; ctrl.abort(); clearTimeout(espera); };
  }, [username, updateBrandingFromUserConfig]);

  // Cerrar sesión: como la de siempre (sidebarmenu.jsx:682), primero se borra la caché de features.
  const salir = useCallback(() => {
    try {
      localStorage.removeItem(llaveCache(username));
      localStorage.removeItem('userFeatures');
    } catch { /* ignorar */ }
    return signOut?.();
  }, [signOut, username]);

  const valor = useMemo(() => {
    const efectivas = recortarPorDominio(features ?? {});
    return {
      user,
      username,
      signOut: salir,
      features: efectivas,
      featuresListas: features !== null,
      featuresDelServidor: featuresDelServidor && recortarPorDominio(featuresDelServidor),
      config,
      error,
      esAdmin: String(config?.role || '').toLowerCase() === 'admin',
      organizacion: {
        nombre: config?.organization?.displayName || null,
        logo: config?.organization?.urlImage || null,
      },
    };
  }, [user, username, salir, features, featuresDelServidor, config, error]);

  return <ConfigUsuarioContext.Provider value={valor}>{children}</ConfigUsuarioContext.Provider>;
}

export function useConfigUsuario() {
  const valor = useContext(ConfigUsuarioContext);
  if (!valor) throw new Error('useConfigUsuario fuera de ConfigUsuarioProvider');
  return valor;
}

/** ¿El usuario tiene el módulo? Los módulos de siempre están prendidos salvo `false` explícito; los
 *  opcionales (optIn) sólo con `true`. Misma regla que ProtectedRoute en App.js. */
export function tieneModulo(features, modulo) {
  if (!modulo.feature) return true;
  const valor = features?.[modulo.feature];
  return modulo.optIn ? valor === true : valor !== false;
}

/** El módulo está APAGADO para el usuario (no opcional y `false` explícito): la de siempre lo mostraba
 *  deshabilitado en el menú con «Este módulo no está disponible para tu cuenta» (sidebarmenu.jsx:50-61). */
export function moduloApagado(features, modulo) {
  return Boolean(modulo.feature) && !modulo.optIn && features?.[modulo.feature] === false
    && !ocultoPorDominio(modulo.feature);
}
